import { Link, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useApi } from '@/hooks/useApi';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import SituacionBadge from '@/components/personal/SituacionBadge';
import { desplazarMes, mesActual, nombreDelMes, type ResumenFichajes, type Situacion } from '@/lib/personal';

type Fila = ResumenFichajes & { id: string; nombreCompleto: string; situacion: Situacion };

/**
 * Los fichajes del mes, por persona.
 *
 * Es la pestaña «Control de fichajes» de la app de lavandería traída aquí, con
 * lo que allí no se puede hacer: cruzar los huecos con las ausencias. Un día
 * sin fichar deja de ser sospechoso en cuanto se sabe que esa persona estaba de
 * vacaciones, y esa columna es la única que se mira de verdad.
 *
 * Solo lectura. Corregir un fichaje se sigue haciendo en la app de housekeeping,
 * que es quien los escribe.
 */
export default function PersonalFichajesPage() {
  const [params, setParams] = useSearchParams();
  const mes = params.get('mes') || mesActual();

  const ponerMes = (m: string) => {
    const siguiente = new URLSearchParams(params);
    siguiente.set('mes', m);
    setParams(siguiente, { replace: true });
  };

  const { data, loading, error } = useApi<{ mes: string; empleados: Fila[] }>(
    () => api.getFichajesEquipo(mes),
    [mes],
  );

  const filas = data?.empleados || [];
  const totales = filas.reduce(
    (acc, f) => ({
      horas: acc.horas + f.horas,
      retrasos: acc.retrasos + f.retrasos,
      sinFichar: acc.sinFichar + f.diasSinFichar.length,
    }),
    { horas: 0, retrasos: 0, sinFichar: 0 },
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">{nombreDelMes(mes)}</h2>
        <div className="flex items-center gap-1">
          <button onClick={() => ponerMes(desplazarMes(mes, -1))} className="btn-secondary px-2.5 py-1 text-sm">←</button>
          <button onClick={() => ponerMes(mesActual())} className="btn-secondary px-2.5 py-1 text-sm">Hoy</button>
          <button
            onClick={() => ponerMes(desplazarMes(mes, 1))}
            disabled={mes >= mesActual()}
            className="btn-secondary px-2.5 py-1 text-sm disabled:opacity-40"
          >
            →
          </button>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <EmptyState title="No se han podido cargar los fichajes" description={error} />
      ) : filas.length === 0 ? (
        <EmptyState
          title="No hay fichajes este mes"
          description="Los fichajes los escribe la app de housekeeping; aquí solo se leen."
        />
      ) : (
        <>
          <div className="card p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <Th align="left">Persona</Th>
                  <Th>Días</Th>
                  <Th>Horas</Th>
                  <Th>Retrasos</Th>
                  <Th>Sin fichar</Th>
                </tr>
              </thead>
              <tbody>
                {filas.map((f) => (
                  <tr key={f.id} className="border-b border-gray-50 hover:bg-sereno-blue/20 transition-colors">
                    <td className="px-4 sm:px-6 py-3">
                      <Link to={`/equipo/personal/${f.id}?tab=fichajes`} className="flex items-center gap-2 hover:underline">
                        <span className="truncate">{f.nombreCompleto}</span>
                        {f.situacion === 'baja' && <SituacionBadge situacion="baja" pequeno />}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{f.diasFichados}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-medium">{f.horas}</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {f.retrasos ? (
                        <span className="text-amber-600">
                          {f.retrasos} <span className="text-xs">({f.retrasoMinutos} min)</span>
                        </span>
                      ) : (
                        <span className="text-sereno-dark/25">—</span>
                      )}
                    </td>
                    <td className="px-4 sm:px-6 py-3 text-right tabular-nums">
                      {f.diasSinFichar.length ? (
                        <span className="text-amber-600 font-medium">{f.diasSinFichar.length}</span>
                      ) : (
                        <span className="text-sereno-dark/25">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-gray-200 font-medium">
                  <td className="px-4 sm:px-6 py-3">Total</td>
                  <td />
                  <td className="px-4 py-3 text-right tabular-nums">{Math.round(totales.horas * 10) / 10}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{totales.retrasos}</td>
                  <td className="px-4 sm:px-6 py-3 text-right tabular-nums">{totales.sinFichar}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <p className="text-[11px] text-sereno-dark/35">
            «Sin fichar» son los días en que esa persona estaba de alta, no fichó y no tenía ninguna ausencia
            registrada. Un tramo abierto —entró y no fichó salida— no suma horas: aparece en su ficha.
          </p>
        </>
      )}
    </div>
  );
}

function Th({ children, align = 'right' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      className={`text-${align} text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider px-4 ${
        align === 'left' ? 'sm:px-6' : ''
      } py-3`}
    >
      {children}
    </th>
  );
}
